import { debounce } from '../shared/utils.js'

/** Where the clicked query waits for the next page load. Shared with assets/js/inline/segment-middleware.js. */
const SEARCH_SOURCE_KEY = 'felix_search_source'

/**
 * Site search dropdown.
 *
 * The debounce / AbortController / response-cache / keyboard-navigation plumbing
 * below is ported from assets/js/components/blog-post-search.js. That is
 * deliberate duplication, not an oversight: roughly 123 of that file's 336 lines
 * genuinely diverge from what this needs — it resolves an external panel by id,
 * renders a flat list of server-rendered cards, replaces the panel while loading,
 * and positions itself as a fixed sheet — so a shared base class would have had
 * subclasses overriding half of it, and extracting one would have changed
 * behaviour in a search that is already live.
 *
 * Unifying the two searches and extracting the shared engine is ticketed. Until
 * then, an ARIA or race-condition fix made here should be made there too.
 *
 * Results are grouped, so the ARIA tree is a listbox of role=group nodes. The
 * group headings carry role=presentation: a bare <p> is not an allowed child of
 * listbox or group, and presentation removes it from the tree while leaving its
 * text available to name the group through aria-labelledby. Arrow keys then skip
 * headings for free, because navigation only ever reads [role="option"].
 */
class SiteSearch extends HTMLElement {
	constructor() {
		super()
		this.items = []
		this.activeIndex = -1
		this.isOpen = false
		this.cache = new Map()
		this.controller = null
		this.popularHTML = ''
		this.lastTracked = ''
	}

	connectedCallback() {
		this.input = this.querySelector('input[role="combobox"]')
		this.panel = this.querySelector('.site-search__panel')
		this.status = this.querySelector('[role="status"]')
		this.clearButton = this.querySelector('.site-search__clear')

		if (!this.input || !this.panel) return

		this.restUrl = this.dataset.restUrl || ''
		this.minChars = parseInt(this.dataset.minChars || '2', 10)
		this.perGroup = parseInt(this.dataset.perGroup || '5', 10)
		this.context = this.dataset.context || 'desktop'
		this.optionIdPrefix = `${this.input.id}-option-`

		// Anonymised unless the setting is explicitly on. Absent, empty or
		// malformed all mean off, so an environment nobody has configured sends no
		// free text -- the safe reading of a missing value, not the permissive one.
		this.sendTerms = this.dataset.sendTerms === '1'

		// The Popular group is server-rendered, so snapshot it and restore the
		// markup verbatim whenever the query clears. Keeping it in PHP means it
		// exists in one place and works before any request is made.
		this.popularHTML = this.panel.innerHTML

		this.fetchDebounced = debounce((q) => this.fetchResults(q), parseInt(this.dataset.debounce || '250', 10))

		this.input.addEventListener('input', this.handleInput)
		this.input.addEventListener('keydown', this.handleKeydown)
		this.input.addEventListener('focus', this.handleFocus)
		this.clearButton?.addEventListener('click', this.handleClear)
		this.panel.addEventListener('mousedown', this.handlePanelMouseDown)
		this.panel.addEventListener('click', this.handleResultClick)
		this.addEventListener('focusout', this.handleFocusOut)
		document.addEventListener('keydown', this.handleDocumentKeydown)
		document.addEventListener('pointerdown', this.handleDocumentPointerDown)

		// Contract with global/mobile-menu.js and global/megamenu.js, which
		// dispatch these on document. Renaming them would break a client running a
		// cached global.min.js, so they keep the historical name.
		document.addEventListener('quick-search:open', this.handleOpenRequest)
		document.addEventListener('quick-search:close', this.handleCloseRequest)

		this.indexOptions()
	}

	disconnectedCallback() {
		this.input?.removeEventListener('input', this.handleInput)
		this.input?.removeEventListener('keydown', this.handleKeydown)
		this.input?.removeEventListener('focus', this.handleFocus)
		this.clearButton?.removeEventListener('click', this.handleClear)
		this.panel?.removeEventListener('mousedown', this.handlePanelMouseDown)
		this.panel?.removeEventListener('click', this.handleResultClick)
		this.removeEventListener('focusout', this.handleFocusOut)
		document.removeEventListener('keydown', this.handleDocumentKeydown)
		document.removeEventListener('pointerdown', this.handleDocumentPointerDown)
		document.removeEventListener('quick-search:open', this.handleOpenRequest)
		document.removeEventListener('quick-search:close', this.handleCloseRequest)
		this.controller?.abort()
	}

	/** Both instances hear the document events; only the rendered one may react. */
	isVisible() {
		return this.offsetParent !== null
	}

	handleOpenRequest = () => {
		if (!this.isVisible()) return
		this.openPanel()
		// setTimeout, not a direct call: iOS will not raise the keyboard for a
		// focus() inside the same task as the tap that opened the drawer.
		setTimeout(() => this.input?.focus({ preventScroll: true }), 0)
	}

	handleCloseRequest = () => this.closePanel()

	handleFocus = () => this.openPanel()

	/**
	 * Empty the box and put the panel back to Popular.
	 *
	 * Setting value in code fires no input event, so the clear control -- which
	 * only hid on typing -- stayed on screen after it had been used. Both callers
	 * had that bug, so both go through here.
	 */
	clearInput() {
		this.input.value = ''
		this.clearButton?.toggleAttribute('hidden', true)
		this.restorePopular()
	}

	handleClear = () => {
		this.clearInput()
		this.input.focus()
	}

	/**
	 * Hand the query to the page the visitor is about to land on.
	 *
	 * The insight wanted is "people reach page X searching for Y", which is a
	 * property of the destination pageview, not of the click -- so the term has to
	 * cross the navigation. sessionStorage rather than a query parameter,
	 * deliberately: a URL reading ?q=herpes would persist the term in browser
	 * history, referrer headers, access logs and every shared link, which is a far
	 * larger exposure than one analytics property. Same-origin always holds,
	 * because every result URL is a Felix permalink.
	 *
	 * Listening for `click` rather than `mousedown` covers the keyboard too: Enter
	 * on the active option calls anchor.click(), so both paths are one code path.
	 *
	 * Popular-list anchors carry no search term and so write nothing -- nobody
	 * searched for those.
	 */
	handleResultClick = (event) => {
		const anchor = event.target.closest('a')

		if (!anchor?.dataset.searchResult) return

		const payload = {
			ts: Date.now(),
			resultType: anchor.dataset.trackResultType || '',
			position: anchor.dataset.trackResultPosition || '',
		}

		if (this.sendTerms && anchor.dataset.trackSearchTerm) {
			payload.term = anchor.dataset.trackSearchTerm
		}

		try {
			sessionStorage.setItem(SEARCH_SOURCE_KEY, JSON.stringify(payload))
		} catch (e) {
			// Private browsing, or storage disabled. Attribution is a nice-to-have.
		}
	}

	handlePanelMouseDown = (event) => {
		// Keep focus in the input so the panel does not close before a click lands.
		if (!event.target.closest('a')) event.preventDefault()
	}

	handleInput = () => {
		const query = this.input.value.trim()

		this.clearButton?.toggleAttribute('hidden', query.length === 0)

		if (query.length < this.minChars) {
			this.controller?.abort()
			this.restorePopular()
			return
		}

		this.openPanel()
		this.fetchDebounced(query)
	}

	handleDocumentKeydown = (event) => {
		if (event.key !== 'Escape' || !this.isOpen) return
		this.clearInput()
		this.closePanel()
		this.input.focus()
	}

	/**
	 * Close when focus leaves the component.
	 *
	 * Pointerdown covers clicking away and Escape covers dismissing, but neither
	 * catches Tab -- so a keyboard user moving into the megamenu left an open
	 * listbox behind them, still announced and still covering the page.
	 *
	 * focusout rather than blur: it bubbles, so it fires for the input, the clear
	 * button and anything else inside. relatedTarget is where focus is heading, and
	 * is null when focus leaves the document altogether (another tab, the address
	 * bar) -- which should close too, so the contains() check handles both.
	 *
	 * Clicking a result does not reach here: handlePanelMouseDown preventDefaults
	 * the mousedown, so focus never leaves the input before the click lands.
	 */
	handleFocusOut = (event) => {
		if (this.contains(event.relatedTarget)) return
		this.closePanel()
	}

	handleDocumentPointerDown = (event) => {
		if (!this.isOpen || this.contains(event.target)) return
		this.closePanel()
	}

	handleKeydown = (event) => {
		switch (event.key) {
			case 'ArrowDown':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex((this.activeIndex + 1) % this.items.length)
				break
			case 'ArrowUp':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex((this.activeIndex - 1 + this.items.length) % this.items.length)
				break
			case 'Home':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex(0)
				break
			case 'End':
				if (this.items.length === 0) return
				event.preventDefault()
				this.setActiveIndex(this.items.length - 1)
				break
			case 'Enter': {
				if (this.items.length === 0) return
				event.preventDefault()
				// With nothing active, highlight the first result rather than
				// navigating: there is no results page to submit to, and guessing a
				// destination the visitor never looked at is worse than a second keypress.
				if (this.activeIndex < 0) {
					this.setActiveIndex(0)
					return
				}
				this.items[this.activeIndex]?.querySelector('a')?.click()
				break
			}
			default:
				break
		}
	}

	/**
	 * Is this still the query in the box?
	 *
	 * A reply is only allowed to paint the panel if the input still holds the text
	 * that asked for it. Backspacing from a long query to a shorter one already in
	 * the cache used to repaint from cache and then be overwritten by the slow
	 * reply for the text the visitor had abandoned.
	 */
	isCurrent(query) {
		return this.input.value.trim() === query
	}

	async fetchResults(query) {
		// Abort first, including on a cache hit. The early return used to skip the
		// abort below, leaving the in-flight request for the abandoned text alive
		// with nothing to stop it landing last.
		this.controller?.abort()

		if (this.cache.has(query)) {
			this.renderResults(this.cache.get(query), query)
			return
		}

		this.controller = new AbortController()
		this.panel.setAttribute('aria-busy', 'true')

		try {
			const url = `${this.restUrl}?s=${encodeURIComponent(query)}&per_group=${this.perGroup}`
			const res = await fetch(url, { signal: this.controller.signal })
			if (!res.ok) throw new Error(`HTTP ${res.status}`)

			const data = await res.json()
			this.cache.set(query, data)

			// Cached above regardless -- the answer is still correct for that text,
			// and keeping it saves the request if the visitor types back to it.
			if (!this.isCurrent(query)) return

			this.renderResults(data, query)
		} catch (err) {
			if (err?.name === 'AbortError') return
			this.renderMessage('Search is unavailable. Please try again.')
			this.announce('Search is unavailable. Please try again.')
		} finally {
			this.panel.removeAttribute('aria-busy')
		}
	}

	restorePopular() {
		this.panel.innerHTML = this.popularHTML
		this.activeIndex = -1
		this.input.removeAttribute('aria-activedescendant')
		this.indexOptions()
	}

	renderMessage(message) {
		this.panel.innerHTML = ''
		const p = document.createElement('p')
		p.className = 'site-search__message text-form-label text-slate-40'
		p.textContent = message
		this.panel.appendChild(p)
		this.items = []
		this.activeIndex = -1
		this.input.removeAttribute('aria-activedescendant')
	}

	renderResults(data, query) {
		const groups = data?.groups || []
		const similar = data?.similar

		// Tracked before the empty branch returns. A search that found nothing is
		// the case with the most product value -- missing content, misspellings,
		// unmet demand -- and a condition query whose groups were all vetoed lands
		// here too, so an early return would make both invisible.
		this.track(data, query)

		if (groups.length === 0) {
			this.renderMessage(`No results for “${query}”.`)
			this.announce(`No results for “${query}”.`)
			return
		}

		this.panel.innerHTML = ''

		groups.forEach((group) => this.panel.appendChild(this.buildGroup(group.label, group.items, group.id, query)))

		if (similar?.items?.length) {
			this.panel.appendChild(this.buildGroup(similar.label, similar.items, 'similar', query))
		}

		this.indexOptions()
		this.activeIndex = -1
		this.input.removeAttribute('aria-activedescendant')

		const count = groups.reduce((n, g) => n + g.items.length, 0)
		const categories = groups.length
		let message = `${count} ${count === 1 ? 'result' : 'results'} in ${categories} ${categories === 1 ? 'category' : 'categories'}`
		if (similar?.items?.length) message += `. ${similar.items.length} similar medications.`
		this.announce(message)
	}

	buildGroup(label, items, id, query) {
		const group = document.createElement('div')
		group.className = 'site-search__group'
		group.setAttribute('role', 'group')
		group.dataset.group = id

		const headingId = `${this.input.id}-group-${id}`
		const heading = document.createElement('p')
		heading.id = headingId
		heading.className = 'site-search__group-heading text-form-label text-slate-40'
		heading.setAttribute('role', 'presentation')
		heading.textContent = label
		group.setAttribute('aria-labelledby', headingId)
		group.appendChild(heading)

		const list = document.createElement('ul')
		list.className = 'site-search__list'
		list.setAttribute('role', 'presentation')

		items.forEach((item, position) => {
			const li = document.createElement('li')
			li.className = 'site-search__option'
			li.setAttribute('role', 'option')
			li.setAttribute('aria-selected', 'false')

			const anchor = document.createElement('a')
			anchor.className = 'site-search__result text-heading-style-16 text-slate-20'
			anchor.href = item.url
			anchor.tabIndex = -1
			anchor.dataset.trackEvent = 'search_result_click'
			anchor.dataset.trackResultType = item.type
			anchor.dataset.trackResultPosition = String(position + 1)
			anchor.dataset.trackCategory = id

			// Not a track-* key, so it never becomes an event property: it marks a
			// searched result apart from a Popular one now that the term, which used
			// to be that marker, may legitimately be absent.
			anchor.dataset.searchResult = '1'

			if (this.sendTerms) {
				anchor.dataset.trackSearchTerm = query
			}

			const title = document.createElement('span')
			title.className = 'site-search__result-title'
			// textContent, never innerHTML: the payload is plain text by contract,
			// and rendering it as text keeps that true at the last step too.
			title.textContent = item.title
			anchor.appendChild(title)

			if (item.subtitle) {
				const subtitle = document.createElement('span')
				subtitle.className = 'site-search__result-subtitle text-body-xs text-slate-40'
				subtitle.textContent = item.subtitle
				anchor.appendChild(subtitle)
			}

			li.appendChild(anchor)
			list.appendChild(li)
		})

		group.appendChild(list)
		return group
	}

	/** Re-index after every render, so ids and order stay flat across groups. */
	indexOptions() {
		this.items = Array.from(this.panel.querySelectorAll('[role="option"]'))
		this.items.forEach((item, index) => {
			item.id = `${this.optionIdPrefix}${index}`
			item.dataset.index = String(index)
		})
	}

	setActiveIndex(index) {
		this.items.forEach((item) => item.setAttribute('aria-selected', 'false'))
		this.activeIndex = index

		const active = this.items[index]
		if (!active) {
			this.input.removeAttribute('aria-activedescendant')
			return
		}

		active.setAttribute('aria-selected', 'true')
		this.input.setAttribute('aria-activedescendant', active.id)

		if (typeof active.scrollIntoView === 'function') {
			active.scrollIntoView({ block: 'nearest' })
		}
	}

	announce(message) {
		if (!this.status || this.status.textContent === message) return
		this.status.textContent = message
	}

	/**
	 * One search_performed per settled search.
	 *
	 * Guarded on the query so a cache hit, a re-render or a second keystroke that
	 * resolves to the same query cannot inflate the count: the 250ms debounce
	 * still resolves several times while someone types a word.
	 */
	track(data, query) {
		if (this.lastTracked === query) return
		this.lastTracked = query

		window.felixTrack?.('search_performed', {
			...(this.sendTerms ? { searchTerm: query } : {}),
			resultCount: data.total,
			resultCategories: (data.groups || []).map((g) => g.id).join(','),
			hadResults: data.total > 0,
			vetoed: Boolean(data.vetoed),
			searchContext: this.context,
		})
	}

	openPanel() {
		if (this.isOpen) return
		this.isOpen = true
		this.panel.setAttribute('aria-hidden', 'false')
		this.panel.classList.add('is-open')
		this.input.setAttribute('aria-expanded', 'true')
		this.classList.add('is-open')
	}

	closePanel() {
		if (!this.isOpen) return
		this.isOpen = false
		this.panel.setAttribute('aria-hidden', 'true')
		this.panel.classList.remove('is-open')
		this.input.setAttribute('aria-expanded', 'false')
		this.input.removeAttribute('aria-activedescendant')
		this.classList.remove('is-open')
		this.activeIndex = -1
	}
}

if (!customElements.get('felix-site-search')) {
	customElements.define('felix-site-search', SiteSearch)
}
